import React, { useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { CallSearchFilter, CallResultsTable, CallDrawerModern, PageLayout, PageHeader } from '../../components'
import { useCallSearchData, useDrawerURL } from '../../hooks'
import useAuthz from '../../hooks/useAuthz'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import { fetchVote } from '../../services/bff'
import { handleUpvote as upvoteHandler, handleDownvote as downvoteHandler } from '../../services/votes'
import logger from '../../utils/logger'
import { formatHandleTimeSeconds } from '../../services/format'
import { formatCallDateET } from '../../services/date'
import styles from './AdminCallList.module.scss'

const AdminCallListPage = ({ userId }) => {
  const { isAdmin } = useAuthz()
  const { t } = useTranslation()

  const callSearchData = useCallSearchData()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedCall, setSelectedCall] = useState(null)

  const [voteStates, setVoteStates] = useState({})
  const currentVote = selectedCall && voteStates[selectedCall.id] ? voteStates[selectedCall.id] : null

  const {
    searchMode,
    campaignName,
    callId,
    startDate,
    endDate,
    formErrors,
    campaigns,
    loadingCampaigns,
    calls,
    loading,
    searched,
    error,
    page,
    totalPages,
    totalCalls,
    sortField,
    sortDirection,
    setCampaignName,
    setCallId,
    setStartDate,
    setEndDate,
    handleSearchModeChange,
    handleSearch,
    handleSort,
    resetSorting,
    handlePageChange,

    disposition,
    direction,
    agentName,
    durationMin,
    durationMax,
    setDisposition,
    setDirection,
    setAgentName,
    setDurationMin,
    setDurationMax,
    filterOptions,

    exporting,
    handleExportCsv
  } = callSearchData

  const handleCampaignNameChange = (value) => {
    setCampaignName(value)
    callSearchData.setFormErrors((prev) => ({ ...prev, campaignName: !value && isAdmin }))
  }

  const handleCallIdChange = (value) => {
    setCallId(value)
    callSearchData.setFormErrors((prev) => ({ ...prev, callId: !value }))
  }

  const formatCallForDrawer = (call) => {
    const formatHandleTime = formatHandleTimeSeconds

    const phoneNumber = call.direction === 'Inbound'
      ? (call.caller_number || call.dnis || 'N/A')
      : (call.dnis || call.caller_number || 'N/A')

    return {
      id: call.call_id,
      0: formatCallDateET(call.call_date),
      1: phoneNumber,
      4: call.direction || 'Unknown',
      5: call.campaign_name || 'Unknown Campaign',
      6: call.agent_name || 'Unknown Agent',
      9: formatHandleTime(call.handle_time),
      13: call.disposition || 'Unknown',
      14: call.call_notes || '',
      17: call.caller_name || '',
      18: call.caller_company || '',
      19: call.caller_email || '',
      caller_email: call.caller_email || '',
      ai_transcript: call.ai_transcript || null,
      ai_summary: call.ai_summary || null,
      sentiment_analysis: call.sentiment_analysis || null,
      qa_evaluation: call.qa_evaluation || null,
      call_date: call.call_date
    }
  }

  const openingFromURLRef = useRef(false)

  const handleOpenFromURL = useCallback(async (callId) => {
    openingFromURLRef.current = true
    try {
      const resp = await authenticatedFetch(
        `${apiEndpoints.calls}?callId=${encodeURIComponent(callId)}&limit=1`
      )
      if (!resp.ok) return
      const data = await resp.json()
      const calls = data.calls || data.data
      if (!calls?.length) return

      const reformattedCall = formatCallForDrawer(calls[0])
      setSelectedCall(reformattedCall)
      setDrawerOpen(true)

      if (userId && callId) {
        try {
          const voteData = await fetchVote(callId, userId)
          setVoteStates(prev => ({
            ...prev,
            [callId]: voteData.vote === 1 ? 'up' : voteData.vote === -1 ? 'down' : null
          }))
        } catch (error) {
          logger.error('Error fetching vote state:', error)
        }
      }
    } catch (err) {
      logger.error('Error opening call from URL:', err)
    } finally {
      openingFromURLRef.current = false
    }
  }, [userId])

  const { openDrawerWithURL, closeDrawerWithURL } = useDrawerURL('call', {
    drawerOpen,
    onOpenFromURL: handleOpenFromURL,
    onClose: () => { setSelectedCall(null); setDrawerOpen(false) }
  })

  const handleDrawerOpen = async (call) => {
    const reformattedCall = formatCallForDrawer(call)
    setSelectedCall(reformattedCall)
    setDrawerOpen(true)

    if (!openingFromURLRef.current) {
      openDrawerWithURL(call.call_id)
    }

    if (userId && call.call_id) {
      try {
        const voteData = await fetchVote(call.call_id, userId)
        setVoteStates(prev => ({
          ...prev,
          [call.call_id]: voteData.vote === 1 ? 'up' : voteData.vote === -1 ? 'down' : null
        }))
      } catch (error) {
        logger.error('Error fetching vote state:', error)
        setVoteStates(prev => ({ ...prev, [call.call_id]: null }))
      }
    }
  }

  const handleDrawerClose = () => {
    setSelectedCall(null)
    setDrawerOpen(false)
    closeDrawerWithURL()
  }

  const handleUpvote = useCallback(async () => {
    if (!selectedCall || !selectedCall.id) return

    const updateVoteState = (callId, vote) => {
      setVoteStates(prev => ({ ...prev, [callId]: vote }))
    }

    await upvoteHandler(selectedCall.id, userId, updateVoteState)
  }, [selectedCall, userId])

  const handleDownvote = useCallback(async () => {
    if (!selectedCall || !selectedCall.id) return

    const updateVoteState = (callId, vote) => {
      setVoteStates(prev => ({ ...prev, [callId]: vote }))
    }

    await downvoteHandler(selectedCall.id, userId, updateVoteState)
  }, [selectedCall, userId])

  return (
    <PageLayout>
      <PageHeader
        title={t('calls.callSearch')}
        subtitle={
          <span data-testid='call-search-subtitle'>
            {t('calls.callSearchSubtitle')}
          </span>
        }
      />

      {/* Unified search + results card */}
      <div className={styles.unifiedCard}>
        <CallSearchFilter
          searchMode={searchMode}
          campaignName={campaignName}
          callId={callId}
          startDate={startDate}
          endDate={endDate}
          formErrors={formErrors}
          campaigns={campaigns}
          loadingCampaigns={loadingCampaigns}
          loading={loading}
          onSearchModeChange={handleSearchModeChange}
          onCampaignNameChange={handleCampaignNameChange}
          onCallIdChange={handleCallIdChange}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={handleSearch}
          disposition={disposition}
          direction={direction}
          agentName={agentName}
          durationMin={durationMin}
          durationMax={durationMax}
          onDispositionChange={setDisposition}
          onDirectionChange={setDirection}
          onAgentNameChange={setAgentName}
          onDurationMinChange={setDurationMin}
          onDurationMaxChange={setDurationMax}
          filterOptions={filterOptions}
        />

        <CallResultsTable
          calls={calls}
          loading={loading}
          searched={searched}
          error={error}
          page={page}
          totalPages={totalPages}
          totalCalls={totalCalls}
          sortField={sortField}
          sortDirection={sortDirection}
          onPageChange={handlePageChange}
          onSort={handleSort}
          onResetSorting={resetSorting}
          onRowClick={handleDrawerOpen}
          onExportCsv={handleExportCsv}
          exporting={exporting}
        />
      </div>

      {/* Call Details Drawer - Modern Card Layout */}
      <CallDrawerModern
        selectedRow={selectedCall}
        drawerOpen={drawerOpen}
        handleCloseDrawerInternal={handleDrawerClose}
        currentVote={currentVote}
        onUpvote={handleUpvote}
        onDownvote={handleDownvote}
        userId={userId}
      />

    </PageLayout>
  )
}

AdminCallListPage.propTypes = {
  userId: PropTypes.string
}

export default AdminCallListPage
